import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { getCategoryByKey } from '@/utils/movie';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = movies.length;

  const go = useCallback(
    (dir: number) => {
      setIndex((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % count), 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const movie = movies[index];
  const category = getCategoryByKey(movie.type);

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
        touchStartX.current = null;
      }}
    >
      {movies.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={m.poster}
            alt={`${m.title}焦点海报`}
            title={`${m.title}高清电影`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>

      <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
        <div className="w-full max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-white">
              {category?.name}
            </span>
            <span className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
              {movie.region} · {movie.year}
            </span>
            <span className="flex items-center gap-1 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
              <i className="ri-star-fill text-accent-400"></i>
              {movie.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-white md:text-5xl">
            {movie.title}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 md:text-base">
            {movie.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${movie.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accent-600 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${movie.type}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-5 py-2.5 text-sm font-bold text-white backdrop-blur-sm transition-colors hover:bg-white/20 cursor-pointer"
            >
              <i className="ri-list-check text-lg"></i>
              更多{category?.name}电影
            </Link>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/30 text-white backdrop-blur-sm transition-colors hover:bg-black/50 cursor-pointer md:left-6 md:h-11 md:w-11"
        aria-label="上一部"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/30 text-white backdrop-blur-sm transition-colors hover:bg-black/50 cursor-pointer md:right-6 md:h-11 md:w-11"
        aria-label="下一部"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 md:bottom-6">
        {movies.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到${m.title}`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-6 bg-accent-500' : 'w-2 bg-white/50 hover:bg-white/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}